import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { WERKSOORT_LABEL, type Werksoort } from '../hooks/useGekozenWerksoort';
import { isoWeekKey, maandagVanWeek, weeknummer } from '../week';
import { useVoorlees } from './Toestandsmelding';

export interface PeriodPickerProps {
  /** Uitsluitend afgesloten weken, nieuwste bovenaan — de kiezer sorteert niet zelf. */
  weken: readonly string[];
  waarde: string;
  onKies: (week: string) => void;
  /** De werksoort van de pagina, als die er een heeft; de naam komt vóór de week in de aankondiging. */
  werksoort?: Werksoort;
}

const DAG = 86_400_000;
/** De pijltekens zijn `aria-hidden`; de knopnaam komt uit `t()`. */
const TEKEN_VORIGE = '‹';
const TEKEN_VOLGENDE = '›';

function naastgelegen(week: string, stap: 1 | -1): string {
  return isoWeekKey(new Date(maandagVanWeek(week).getTime() + stap * 7 * DAG));
}

/** True als de taal de maand vóór de dag zet ("October 19"); dan kan de maand niet weg bij de eerste dag. */
function maandVoorDag(locale: string): boolean {
  const delen = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).formatToParts(new Date(0)).map((deel) => deel.type);
  return delen.indexOf('month') < delen.indexOf('day');
}

/**
 * "19 tot en met 25 oktober 2026": maand en jaar staan er één keer als ze gelijk zijn. In een taal die
 * de maand vóór de dag zet blijft de maand bij de eerste dag staan ("October 19 to October 25, 2026").
 */
function weekdagen(week: string, locale: string, t: TFunction): string {
  const maandag = maandagVanWeek(week);
  const zondag = new Date(maandag.getTime() + 6 * DAG);
  const zelfdeJaar = maandag.getUTCFullYear() === zondag.getUTCFullYear();
  const zelfdeMaand = zelfdeJaar && maandag.getUTCMonth() === zondag.getUTCMonth() && !maandVoorDag(locale);
  const van = maandag.toLocaleDateString(locale, {
    day: 'numeric',
    ...(zelfdeMaand ? {} : { month: 'long' }),
    ...(zelfdeJaar ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
  const tot = zondag.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  return t('qa.bediening.weekkiezer.dagen', { van, tot });
}

/**
 * De Weekkiezer (EXPERIENCE.md "Overal"): ‹ en › schuiven één week, binnen de aangeboden weken.
 *
 * Geen "deze week" en geen vrije datumkiezer (UX-DR16): een halve week meten levert een halve uitkomst
 * die als een hele leest. Aan de rand blijft de pijl focusbaar met `aria-disabled` en zegt het
 * Voorleesgebied waarom er niets gebeurt; een uitgeschakelde knop zou de focus laten wegvallen. Na een
 * wissel blijft de focus op de bediende pijl, want de kiezer zelf wordt niet opnieuw opgebouwd.
 */
export function PeriodPicker({ weken, waarde, onKies, werksoort }: PeriodPickerProps) {
  const { t, i18n } = useTranslation();
  const voorlees = useVoorlees();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  if (weken.length === 0) {
    return (
      <p className="text-[0.8125rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-geen-afgesloten-week">
        {t('qa.week.geenAfgeslotenWeek')}
      </p>
    );
  }

  const plek = weken.indexOf(waarde);
  const isNieuwste = plek <= 0;
  const isOudste = plek === -1 || plek === weken.length - 1;
  const vorige = naastgelegen(waarde, -1);
  const volgende = naastgelegen(waarde, 1);

  function kies(week: string) {
    onKies(week);
    const nummer = weeknummer(week);
    const dagen = weekdagen(week, locale, t);
    voorlees(
      werksoort === undefined
        ? t('qa.bediening.weekkiezer.gewisseld', { week: nummer, dagen })
        : t('qa.bediening.weekkiezer.gewisseldMetWerksoort', { werksoort: t(WERKSOORT_LABEL[werksoort]), week: nummer, dagen }),
    );
  }

  const pijl = `inline-flex min-h-11 min-w-11 items-center justify-center rounded-[var(--radius-control)] text-lg ${FOCUSRING}`;
  const actief = 'text-[var(--foreground-heading)] hover:bg-[var(--background-muted)]';
  const rand = 'cursor-default text-[var(--foreground-muted)]';

  return (
    <div
      className="flex w-fit max-w-full items-center gap-1 rounded-lg border border-[var(--border)] bg-[var(--background)] p-1 max-md:w-full"
      data-testid="werkkwaliteit-weekkiezer"
    >
      <button
        aria-disabled={isOudste ? 'true' : undefined}
        aria-label={t('qa.bediening.weekkiezer.vorige', { week: weeknummer(vorige) })}
        className={`${pijl} ${isOudste ? rand : actief}`}
        data-testid="werkkwaliteit-week-vorige"
        onClick={() => (isOudste ? voorlees(t('qa.bediening.weekkiezer.randOudste')) : kies(weken[plek + 1]!))}
        type="button"
      >
        <span aria-hidden="true">{TEKEN_VORIGE}</span>
      </button>
      <p className="min-w-0 flex-1 px-2 text-center text-[0.8125rem] text-[var(--foreground)]">
        <strong className="font-semibold text-[var(--foreground-heading)]">{t('qa.bediening.weekkiezer.titel', { week: weeknummer(waarde) })}</strong>{' '}
        <span className="text-[var(--foreground-muted)]">
          {plek === 0
            ? t('qa.bediening.weekkiezer.dagenLaatstAfgesloten', { dagen: weekdagen(waarde, locale, t) })
            : weekdagen(waarde, locale, t)}
        </span>
      </p>
      <button
        aria-disabled={isNieuwste ? 'true' : undefined}
        aria-label={t('qa.bediening.weekkiezer.volgende', { week: weeknummer(volgende) })}
        className={`${pijl} ${isNieuwste ? rand : actief}`}
        data-testid="werkkwaliteit-week-volgende"
        onClick={() => (isNieuwste ? voorlees(t('qa.bediening.weekkiezer.randNieuwste')) : kies(weken[plek - 1]!))}
        type="button"
      >
        <span aria-hidden="true">{TEKEN_VOLGENDE}</span>
      </button>
    </div>
  );
}
